import React, {
  useState,
  useImperativeHandle,
  forwardRef,
  useCallback,
} from "react";
import { AnimatePresence, motion } from "framer-motion";
import { FaAngleLeft } from "react-icons/fa";
import clsx from "clsx";
import MapDrawerBase from "./MapDrawerBase";

export const MapDrawer = forwardRef(
  (
    {
      openButtonLabel = "Abrir",
      onOpen,
      onClose,
      isOpen: isDrawerOpenProp,
      setIsOpen: setIsDrawerOpenProp,
      children,
      position = "top-right",
      icon: Icon = FaAngleLeft,
      size = "md",
      variant = "solid",
    },
    ref,
  ) => {
    const [internalDrawerOpen, setInternalDrawerOpen] = useState(false);

    const isDrawerOpen =
      isDrawerOpenProp !== undefined ? isDrawerOpenProp : internalDrawerOpen;

    const setIsDrawerOpen = setIsDrawerOpenProp || setInternalDrawerOpen;

    const open = useCallback(() => {
      if (!isDrawerOpen) {
        setIsDrawerOpen(true);
        onOpen?.();
      }
    }, [isDrawerOpen, setIsDrawerOpen, onOpen]);

    const close = useCallback(() => {
      if (isDrawerOpen) {
        setIsDrawerOpen(false);
        onClose?.();
      }
    }, [isDrawerOpen, setIsDrawerOpen, onClose]);

    const toggle = useCallback(() => {
      if (isDrawerOpen) close();
      else open();
    }, [isDrawerOpen, open, close]);

    // 🔥 API IMPERATIVA OFICIAL
    useImperativeHandle(
      ref,
      () => ({
        open,
        close,
        toggle,
        isOpen: () => isDrawerOpen,
      }),
      [open, close, toggle, isDrawerOpen],
    );

    const positionClasses = {
      "top-left": "top-4 left-4",
      "top-right": "top-4 right-4",
      "bottom-left": "bottom-4 left-4",
      "bottom-right": "bottom-4 right-4",
    };

    const sizeClasses = {
      sm: "px-2 py-1 text-sm",
      md: "px-4 py-2 text-base",
      lg: "px-6 py-3 text-lg",
    };

    const variantClasses = {
      solid: "bg-white text-black shadow",
      outline: "border border-gray-300 bg-white/80 backdrop-blur text-black",
      ghost: "bg-white/40 backdrop-blur shadow-sm",
    };

    const isDefaultArrow = Icon === FaAngleLeft;

    return (
      <>
        <MapDrawerBase isOpen={isDrawerOpen} onClose={close}>
          <div className="flex h-full flex-col overflow-y-auto">{children}</div>
        </MapDrawerBase>

        <AnimatePresence>
          {!isDrawerOpen && (
            <motion.button
              onClick={open}
              type="button"
              className={clsx(
                "absolute z-[500] flex items-center gap-2 rounded transition-all",
                positionClasses[position],
                sizeClasses[size],
                variantClasses[variant],
              )}
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.3 }}
            >
              <span className="hidden items-center gap-2 sm:flex">
                {!isDefaultArrow && <Icon />}
                {openButtonLabel}
              </span>
              <span className="inline sm:hidden">
                <Icon />
              </span>
            </motion.button>
          )}
        </AnimatePresence>
      </>
    );
  },
);

MapDrawer.displayName = "MapDrawer";
